import { useRef, type FC } from 'react';
import {
  NumericAxis,
  NumberRange,
  EAutoRange,
  XyDataSeries,
  XyyDataSeries,
  XyScatterRenderableSeries,
  FastBandRenderableSeries,
  EllipsePointMarker,
  NumericLabelProvider,
  NativeTextAnnotation,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  MouseWheelZoomModifier,
  RubberBandXyZoomModifier,
  YAxisDragModifier,
  EXyDirection,
  ChartModifierBase2D,
  type ModifierMouseArgs,
  type IPointMetadata,
} from 'scichart';
import {
  SciChartView,
  AXIS_TITLE_FONT_SIZE,
  AXIS_LABEL_FONT_SIZE,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { formatNumber } from 'utils/number';
import type { PreDrillGunBarrelResponse } from 'types/api-responses';
import type { ColorByRule } from 'components/color-by-attribute-modal';
import {
  addSharedDepthAxes,
  setSharedDepthRange,
  TVDSS_AXIS_ID,
} from './subsurface-chart-axes';
import {
  formationBandFill,
  formationBandStroke,
} from './subsurface-formation-style';
import {
  buildFormationBands,
  buildTypeCurveFormationLabels,
  colorForBoreUwi,
  groundElevationDatumFt,
} from './utils';
import styles from './subsurface-chart.module.scss';

// Gun-barrel response is the result itself (no `results` envelope).
type GunBarrelResult = PreDrillGunBarrelResponse;

interface PreDrillGunBarrelChartProps {
  result: GunBarrelResult | null;
  tvdssDomain: [number, number] | null;
  isLoading: boolean;
  /** Color-by rules whose Apply-To includes the Gun Barrel, matched by UWI. */
  colorRules: ColorByRule[];
  /** Resolved `${filterId}:${value}` → uwi[] membership for attribute rules. */
  colorByUwiMap: Record<string, string[]>;
  /** Workspace 14-digit UWI (the subject), for Well-Qualifier matching. */
  subjectUwi: string;
}

const OFFSET_PX = 11;
const SUBJECT_PX = 14; // subject marker slightly larger so it reads as primary
const OFFSET_SERIES_ID = 'gb-offsets';
const SUBJECT_SERIES_ID = 'gb-subject';
const DEFAULT_OFFSET = '#0f62fe'; // Carbon blue 60
const SUBJECT_FILL = '#161616'; // subject (workspace) well — near-black
// Offset wells split into one scatter series per color; any id with this
// prefix is a hover-testable offset series.
const OFFSET_SERIES_PREFIX = `${OFFSET_SERIES_ID}-`;
const fmt = (v: number) => formatNumber(v, 0, 0);

interface WellMarkerMeta extends IPointMetadata {
  /** Bold header — uwi14 (or 10-digit uwi when uwi14 is absent). */
  uwiLabel: string;
  /** Well name + landing zone (type-curve formation); shown when present. */
  wellName: string | null;
  landingZone: string | null;
}

// DOM hover tooltip over offset markers — shows the well name.
class WellHoverModifier extends ChartModifierBase2D {
  readonly type = 'PreDrillWellHover';
  private tooltipEl: HTMLDivElement | null = null;
  private readonly hitRadius = OFFSET_PX / 2;

  setTooltip(el: HTMLDivElement | null) {
    this.tooltipEl = el;
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    super.modifierMouseMove(args);
    if (!this.tooltipEl || !this.parentSurface) return;
    const renderables = this.parentSurface.renderableSeries.asArray();
    let hitSeries: (typeof renderables)[number] | null = null;
    let hitIndex = -1;
    // Hit-test subject + every per-color offset series; first hit wins.
    const wellSeries = renderables.filter(
      (s) => s.id === SUBJECT_SERIES_ID || s.id.startsWith(OFFSET_SERIES_PREFIX)
    );
    for (const series of wellSeries) {
      const provider = series?.hitTestProvider;
      if (!series || !provider) continue;
      const ht = provider.hitTest(
        args.mousePoint.x,
        args.mousePoint.y,
        this.hitRadius
      );
      if (
        ht.isHit &&
        ht.dataSeriesIndex >= 0 &&
        ht.distance <= this.hitRadius
      ) {
        hitSeries = series;
        hitIndex = ht.dataSeriesIndex;
        break;
      }
    }
    if (!hitSeries || hitIndex < 0) {
      this.tooltipEl.style.display = 'none';
      return;
    }
    const meta = (hitSeries.dataSeries as XyDataSeries).getMetadataAt(
      hitIndex
    ) as WellMarkerMeta | null;
    if (!meta) {
      this.tooltipEl.style.display = 'none';
      return;
    }
    // uwi (bold) + name + landing zone, each on its own line when present.
    const lines = [`<b>${meta.uwiLabel || '—'}</b>`];
    if (meta.wellName) lines.push(meta.wellName);
    if (meta.landingZone) lines.push(`Landing Zone: ${meta.landingZone}`);
    this.tooltipEl.innerHTML = lines.join('<br/>');
    this.tooltipEl.style.display = 'block';
    const rect = this.tooltipEl.parentElement?.getBoundingClientRect();
    if (rect) {
      const e = args.nativeEvent as MouseEvent;
      this.tooltipEl.style.left = `${e.clientX - rect.left + 12}px`;
      this.tooltipEl.style.top = `${e.clientY - rect.top + 12}px`;
    }
  }

  override modifierMouseLeave(args: ModifierMouseArgs): void {
    super.modifierMouseLeave(args);
    if (this.tooltipEl) this.tooltipEl.style.display = 'none';
  }
}

// Gun Barrel cross-section: formation bands + subject + offset wells at their
// perpendicular offset, painted by Gun-Barrel color rules (matched by UWI).
export const PreDrillGunBarrelChart: FC<PreDrillGunBarrelChartProps> = ({
  result,
  tvdssDomain,
  isLoading,
  colorRules,
  colorByUwiMap,
  subjectUwi,
}) => {
  const dataRef = useRef(result);
  dataRef.current = result;
  const domainRef = useRef(tvdssDomain);
  domainRef.current = tvdssDomain;
  const colorsRef = useRef({ colorRules, colorByUwiMap, subjectUwi });
  colorsRef.current = { colorRules, colorByUwiMap, subjectUwi };
  const elevRef = useRef(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef<WellHoverModifier | null>(null);

  const initChart = ({ surface, wasmContext }: SciChartInitContext) => {
    const xLabel = new NumericLabelProvider();
    xLabel.formatLabel = fmt;
    surface.xAxes.add(
      new NumericAxis(wasmContext, {
        axisTitle: 'Offset (ft)',
        axisTitleStyle: { fontSize: AXIS_TITLE_FONT_SIZE },
        labelStyle: { fontSize: AXIS_LABEL_FONT_SIZE },
        autoRange: EAutoRange.Never,
        drawMinorGridLines: false,
        labelProvider: xLabel,
      })
    );
    addSharedDepthAxes(surface, wasmContext, elevRef);

    // Y-axis (depth) zoom only — the X axis stays fixed.
    surface.chartModifiers.add(
      new RubberBandXyZoomModifier({ xyDirection: EXyDirection.YDirection })
    );
    surface.chartModifiers.add(
      new MouseWheelZoomModifier({ xyDirection: EXyDirection.YDirection })
    );
    surface.chartModifiers.add(new YAxisDragModifier());
    const hover = new WellHoverModifier();
    hover.receiveHandledEvents = true;
    surface.chartModifiers.add(hover);
    hoverRef.current = hover;
  };

  const renderData = ({ surface, wasmContext }: SciChartInitContext) => {
    surface.renderableSeries.clear();
    surface.annotations.clear();
    const model = dataRef.current;
    if (!model) return;
    const {
      colorRules: rules,
      colorByUwiMap: uwiMap,
      subjectUwi: subject,
    } = colorsRef.current;
    // Resolve a well's Gun-Barrel color from the active rules; default offset
    // otherwise.
    const colorOf = (uwi: string | null | undefined): string =>
      colorForBoreUwi(uwi, subject, rules, uwiMap, 'gunBarrel') ??
      DEFAULT_OFFSET;

    // Nearby wells come from the `wells` POI layer.
    const wells =
      model.poiLayers?.find((layer) => layer.kind === 'wells')?.wells ?? [];

    // Center the cross-section on the subject (x = 0) and span the offset wells.
    const xs = wells.map((w) => w.xOffsetFt);
    const allX = xs.length > 0 ? [0, ...xs] : [-2640, 2640];
    const xMin = Math.min(...allX);
    const xMax = Math.max(...allX);
    const xPad = Math.max(500, (xMax - xMin) * 0.15);
    const bandX = [xMin - xPad, xMax + xPad];

    // Formation bands — each grid extends down to the next formation's top
    // (continuous column), like the Subsurface Explorer.
    buildFormationBands(model.formationHorizontals?.formations ?? []).forEach(
      (band) => {
        surface.renderableSeries.add(
          new FastBandRenderableSeries(wasmContext, {
            yAxisId: TVDSS_AXIS_ID,
            dataSeries: new XyyDataSeries(wasmContext, {
              xValues: bandX,
              yValues: [band.topTvdss, band.topTvdss],
              y1Values: [band.bottomTvdss, band.bottomTvdss],
            }),
            fill: formationBandFill(band.color, band.name),
            fillY1: formationBandFill(band.color, band.name),
            stroke: formationBandStroke(band.color),
            strokeY1: formationBandStroke(band.color),
            strokeThickness: 1,
          })
        );
        // Right-margin geologic-interval label, mid-band.
        surface.annotations.add(
          new NativeTextAnnotation({
            x1: 1,
            y1: (band.topTvdss + band.bottomTvdss) / 2,
            xCoordinateMode: ECoordinateMode.Relative,
            yAxisId: TVDSS_AXIS_ID,
            text: band.geologicInterval ?? band.name,
            fontSize: 8,
            textColor: '#161616',
            horizontalAnchorPoint: EHorizontalAnchorPoint.Right,
            verticalAnchorPoint: EVerticalAnchorPoint.Center,
          })
        );
      }
    );

    // Left-margin type-curve formation names, in each formation's color, at
    // the interval mid-depth.
    buildTypeCurveFormationLabels(
      model.formationHorizontals?.typeCurveFormations ?? []
    ).forEach((label) => {
      surface.annotations.add(
        new NativeTextAnnotation({
          x1: 0,
          y1: label.midTvdss,
          xCoordinateMode: ECoordinateMode.Relative,
          yAxisId: TVDSS_AXIS_ID,
          text: label.name,
          fontSize: 9,
          textColor: label.color,
          horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
          verticalAnchorPoint: EVerticalAnchorPoint.Center,
        })
      );
    });

    // One scatter point: chart X (offset ft), Y (TVDSS), + hover fields.
    interface MarkerPoint {
      x: number;
      y: number;
      uwiLabel: string;
      wellName: string | null;
      landingZone: string | null;
    }
    const addScatter = (
      id: string,
      points: MarkerPoint[],
      fill: string,
      px: number
    ): void => {
      if (points.length === 0) return;
      surface.renderableSeries.add(
        new XyScatterRenderableSeries(wasmContext, {
          id,
          yAxisId: TVDSS_AXIS_ID,
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: points.map((p) => p.x),
            yValues: points.map((p) => p.y),
            metadata: points.map((p) => ({
              isSelected: false,
              uwiLabel: p.uwiLabel,
              wellName: p.wellName,
              landingZone: p.landingZone,
            })),
          }),
          pointMarker: new EllipsePointMarker(wasmContext, {
            width: px,
            height: px,
            fill,
            stroke: '#ffffff',
            strokeThickness: 1,
          }),
        })
      );
    };

    // Offset (neighbour) wells — markers are per-series, so group by resolved
    // color into one scatter series each. Hover shows uwi + name + zone.
    const offsetsByColor = new Map<string, MarkerPoint[]>();
    wells.forEach((w) => {
      const point: MarkerPoint = {
        x: w.xOffsetFt,
        y: w.tvdssFt,
        uwiLabel: w.uwi14 ?? w.uwi,
        wellName: w.name ?? null,
        landingZone: w.typeCurveFormation ?? null,
      };
      const fill = colorOf(w.uwi14 ?? w.uwi);
      const group = offsetsByColor.get(fill);
      if (group) group.push(point);
      else offsetsByColor.set(fill, [point]);
    });
    let offsetSeriesIdx = 0;
    offsetsByColor.forEach((points, fill) => {
      addScatter(
        `${OFFSET_SERIES_PREFIX}${offsetSeriesIdx}`,
        points,
        fill,
        OFFSET_PX
      );
      offsetSeriesIdx += 1;
    });

    // Subject bore — slightly larger, plotted from the slice eval point's
    // `tvdssFt`. Near-black unless a Gun-Barrel rule matches its own UWI.
    const subjectEval = model.subject?.evalPoints?.[0];
    // Ground-level (KB) datum for the left TVD axis, from the subject sample:
    // TVDSS + TVD recovers the surface elevation; the axis labels
    // TVD = datum − TVDSS. Without this the datum stays 0 and TVD reads
    // identically to TVDSS.
    const datum = groundElevationDatumFt(
      subjectEval?.tvdssFt,
      subjectEval?.tvdFt
    );
    if (datum !== null) elevRef.current = datum;
    if (subjectEval && Number.isFinite(subjectEval.tvdssFt)) {
      const subjectId = model.subject?.uwi14 ?? model.subject?.uwi ?? subject;
      addScatter(
        SUBJECT_SERIES_ID,
        [
          {
            x: subjectEval.xOffsetFt ?? 0,
            y: subjectEval.tvdssFt,
            uwiLabel: model.subject?.uwi14 ?? model.subject?.uwi ?? '—',
            wellName: null,
            landingZone: null,
          },
        ],
        colorForBoreUwi(subjectId, subject, rules, uwiMap, 'gunBarrel') ??
          SUBJECT_FILL,
        SUBJECT_PX
      );
    }

    surface.xAxes.get(0).visibleRange = new NumberRange(bandX[0], bandX[1]);
    setSharedDepthRange(surface, domainRef.current);
    hoverRef.current?.setTooltip(tooltipRef.current);
  };

  return (
    <SciChartView
      initChart={initChart}
      renderData={renderData}
      renderDeps={[result, tvdssDomain, colorRules, colorByUwiMap, subjectUwi]}
      isLoading={isLoading}
      showResetZoom
      height="100%"
      exportConfig={{
        label: 'Gun Barrel',
        filenamePrefix: 'pre-drill-gun-barrel',
      }}
    >
      <div ref={tooltipRef} className={styles.tooltip} />
    </SciChartView>
  );
};
